import { useEffect, useRef } from 'react';

import { isAppError } from '@/api/errors';
import { env } from '@/config/env';
import { jitter } from '@/utils/jitter';

const GROWTH = 1.5;
const FAILURE_LIMIT = 3;

export interface UsePollingOptions {
  enabled: boolean;
  onTick: (signal: AbortSignal) => Promise<void>;
  /** Reset backoff when this changes (typically deposit status). */
  resetKey: string;
}

/**
 * One poll timer app-wide. Backoff ×1.5 on an unchanged schedule, cap at `pollMaxMs`, ±15% jitter.
 * Pauses on hidden and offline. Never a decision maker — `onTick` writes server truth; the selector
 * re-derives the view.
 *
 * The first scheduled wait is `pollBaseMs` (4s). Growth applies after each subsequent tick, so the
 * delays are 4 → 6 → 9 → 13.5 → 20 → 20. An immediate poll on mount / visibility / online does not
 * consume a delay slot.
 */
export function usePolling({ enabled, onTick, resetKey }: UsePollingOptions): void {
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  const intervalRef = useRef(env.pollBaseMs);
  const failuresRef = useRef(0);
  const inFlightRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    intervalRef.current = env.pollBaseMs;
    failuresRef.current = 0;
  }, [resetKey]);

  useEffect(() => {
    let cancelled = false;
    let stopped = false;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const abortInFlight = () => {
      inFlightRef.current?.abort();
      inFlightRef.current = null;
    };

    const poll = async (): Promise<'ok' | 'stop' | 'aborted'> => {
      abortInFlight();
      const controller = new AbortController();
      inFlightRef.current = controller;
      try {
        await onTickRef.current(controller.signal);
        failuresRef.current = 0;
        return 'ok';
      } catch (error) {
        if (controller.signal.aborted || cancelled) return 'aborted';
        if (isAppError(error) && error.status === 401) return 'stop';
        if (isAppError(error) && error.status === 429) {
          intervalRef.current = env.pollMaxMs;
        } else if (isAppError(error) && !error.retryable) {
          failuresRef.current += 1;
          if (failuresRef.current >= FAILURE_LIMIT) return 'stop';
        }
        return 'ok';
      } finally {
        if (inFlightRef.current === controller) inFlightRef.current = null;
      }
    };

    const schedule = (grow: boolean) => {
      clearTimer();
      if (cancelled || stopped || !enabled) return;
      if (document.visibilityState === 'hidden') return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      if (grow) {
        intervalRef.current = Math.min(intervalRef.current * GROWTH, env.pollMaxMs);
      }
      const delay = jitter(intervalRef.current, 0.15);
      timerRef.current = setTimeout(() => {
        void poll().then((result) => {
          if (result === 'stop') {
            stopped = true;
            clearTimer();
            return;
          }
          if (result === 'aborted') return;
          schedule(true);
        });
      }, delay);
    };

    const kickoff = () => {
      void poll().then((result) => {
        if (result === 'stop') {
          stopped = true;
          return;
        }
        if (result === 'aborted') return;
        schedule(false);
      });
    };

    if (!enabled) {
      abortInFlight();
      clearTimer();
      return;
    }

    kickoff();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        intervalRef.current = env.pollBaseMs;
        kickoff();
      } else {
        abortInFlight();
        clearTimer();
      }
    };

    const onOffline = () => {
      abortInFlight();
      clearTimer();
    };

    const onOnline = () => {
      intervalRef.current = env.pollBaseMs;
      kickoff();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      stopped = true;
      abortInFlight();
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled]);
}
